import { toast } from 'react-toastify';
import { api } from '~/components/Providers/APIClient';
import {
    levelCustomCopyFormInput,
    levelCustomCopyFormSchema,
    levelMoveFormInput,
    levelNoteFormInput,
    levelNoteFormSchema,
    levelPlaceFormInput,
    levelPlaceFormSchema,
    levelEditFormInput,
    levelEditFormSchema,
    levelUpdateFormInput,
    levelUpdateFormSchema,
} from '~/util/forms/level';
import { ApiMutationOptions } from '~/util/hooks/useApiMutation';
import {
    LevelCustomCopy,
    LevelNote,
    LevelUpdate,
    ResolvedLevel,
} from '~/util/types/levels';
import { BaseUser } from '~/util/types/users';

export const newLevelMutation = (): ApiMutationOptions<
    ResolvedLevel,
    levelPlaceFormInput
> => ({
    invalidateQueries: ({ variables: { list } }) => ['list', list],
    toastMessages: {
        loading: 'Creating level...',
        error: 'Failed to create level: ',
        success: 'Level created successfully',
    },
    customMutationFn: async (values) => {
        const { list, level, creators, verification } =
            levelPlaceFormSchema.parse(values);

        const levelResponse = await api.send<ResolvedLevel>({
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/levels`,
            method: 'POST',
            attachAuth: true,
            body: level,
        });

        if (levelResponse.error) {
            throw levelResponse;
        }

        const [creatorResponse, verificationResponse] = await Promise.all([
            api.send<BaseUser[]>({
                path: `/${list === 'platformer' ? 'arepl' : 'aredl'}/levels/${levelResponse.data.id}/creators`,
                method: 'POST',
                body: creators,
                attachAuth: true,
            }),
            api.send({
                path: `/${list === 'platformer' ? 'arepl' : 'aredl'}/records/`,
                method: 'POST',
                body: {
                    level_id: levelResponse.data.id,
                    hide_video: false,
                    ...verification,
                },
                attachAuth: true,
            }),
        ]);

        if (creatorResponse.error) {
            toast.error(
                `Failed to add creators to the new level: ${creatorResponse.data.message}`,
                {
                    autoClose: 10000,
                },
            );
        }

        if (verificationResponse.error) {
            console.error(verificationResponse);
            toast.error(
                `Error adding verification record to the new level: ${verificationResponse.data.message}`,
                {
                    autoClose: 10000,
                },
            );
        }

        return levelResponse;
    },
});

export type updateLevelMutationType = {
    data: levelEditFormInput;
    levelid: string;
};
export const updateLevelMutation = (): ApiMutationOptions<
    ResolvedLevel,
    updateLevelMutationType
> => ({
    invalidateQueries: ({
        variables: {
            data: { list },
            levelid,
        },
    }) => [
        ['list', list],
        ['list', list, levelid, 'base'],
        ['list', list, levelid, 'creators'],
    ],
    toastMessages: {
        loading: 'Updating level...',
        error: 'Failed to update level: ',
        success: 'Level updated successfully',
    },
    customMutationFn: async ({ data, levelid }) => {
        const { list, level, creators } = levelEditFormSchema.parse(data);

        const levelResponse = await api.send<ResolvedLevel>({
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/levels/${levelid}`,
            method: 'PATCH',
            attachAuth: true,
            body: level,
        });

        if (levelResponse.error) {
            throw levelResponse;
        }

        const creatorResponse = await api.send<BaseUser[]>({
            path: `/${data.list === 'platformer' ? 'arepl' : 'aredl'}/levels/${levelid}/creators`,
            method: 'POST',
            body: creators,
            attachAuth: true,
        });

        if (creatorResponse.error) {
            toast.error(
                `Failed to update creators: ${creatorResponse.data.message}`,
                {
                    autoClose: 10000,
                },
            );
        }
        return levelResponse;
    },
});

export type moveLevelMutationType = {
    data: levelMoveFormInput;
    levelid: string;
};
export const moveLevelMutation = (): ApiMutationOptions<
    ResolvedLevel,
    moveLevelMutationType
> => ({
    buildRequest: ({
        data: {
            list,
            level: { status, position },
        },
        levelid,
    }) => {
        return {
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/levels/${levelid}`,
            method: 'PATCH',
            attachAuth: true,
            body: { status, position },
        };
    },
    invalidateQueries: ({
        variables: {
            data: { list },
            levelid,
        },
    }) => [
        ['list', list],
        ['list', list, levelid, 'base'],
    ],
    toastMessages: {
        loading: 'Moving level...',
        error: 'Failed to move level: ',
        success: 'Level moved successfully',
    },
});

export type createCustomCopyMutationType = {
    levelid: string;
    list: 'classic' | 'platformer';
    data: levelCustomCopyFormInput;
};
export const createCustomCopyMutation = (): ApiMutationOptions<
    LevelCustomCopy,
    createCustomCopyMutationType
> => ({
    buildRequest: ({ levelid, list, data }) => {
        return {
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/levels/${levelid}/custom-copies`,
            method: 'POST',
            attachAuth: true,
            body: levelCustomCopyFormSchema.parse(data),
        };
    },
    invalidateQueries: ({ variables: { list, levelid } }) => [
        'list',
        list,
        levelid,
        'level_custom_copies',
    ],
    toastMessages: {
        loading: 'Creating ID...',
        error: 'Failed to create ID: ',
        success: 'ID created successfully',
    },
});

export type updateCustomCopyMutationType = {
    copy_id: string;
    levelid: string;
    list: 'classic' | 'platformer';
    data: levelCustomCopyFormInput;
};
export const updateCustomCopyMutation = (): ApiMutationOptions<
    LevelCustomCopy,
    updateCustomCopyMutationType
> => ({
    buildRequest: ({ copy_id, levelid, list, data }) => {
        return {
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/levels/${levelid}/custom-copies/${copy_id}`,
            method: 'PATCH',
            attachAuth: true,
            body: levelCustomCopyFormSchema.parse(data),
        };
    },
    invalidateQueries: ({ variables: { list, levelid } }) => [
        ['list', list, levelid, 'level_custom_copies'],
    ],
    toastMessages: {
        loading: 'Updating ID...',
        error: 'Failed to update ID: ',
        success: 'ID updated successfully',
    },
});

export type deleteCustomCopyMutationType = {
    copy_id: string;
    levelid: string;
    list: 'classic' | 'platformer';
};
export const deleteCustomCopyMutation = (): ApiMutationOptions<
    LevelCustomCopy,
    deleteCustomCopyMutationType
> => ({
    buildRequest: ({ copy_id, levelid, list }) => {
        return {
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/levels/${levelid}/custom-copies/${copy_id}`,
            method: 'DELETE',
            attachAuth: true,
        };
    },
    invalidateQueries: ({ variables: { list, levelid } }) => [
        ['list', list, levelid, 'level_custom_copies'],
    ],

    toastMessages: {
        loading: 'Deleting ID...',
        error: 'Failed to delete ID: ',
        success: 'ID deleted successfully',
    },
});

export type createNoteMutationType = {
    levelid: string;
    list: 'classic' | 'platformer';
    data: levelNoteFormInput;
};
export const createNoteMutation = (): ApiMutationOptions<
    LevelNote,
    createNoteMutationType
> => ({
    buildRequest: ({ levelid, list, data }) => {
        return {
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/levels/${levelid}/notes`,
            method: 'POST',
            attachAuth: true,
            body: levelNoteFormSchema.parse(data),
        };
    },
    invalidateQueries: ({ variables: { list, levelid } }) => [
        ['list', list, levelid, 'level_notes'],
    ],
    toastMessages: {
        loading: 'Creating note...',
        error: 'Failed to create note: ',
        success: 'Note created successfully',
    },
});

export type updateNoteMutationType = {
    note_id: string;
    levelid: string;
    list: 'classic' | 'platformer';
    data: levelNoteFormInput;
};
export const updateNoteMutation = (): ApiMutationOptions<
    LevelNote,
    updateNoteMutationType
> => ({
    buildRequest: ({ note_id, levelid, list, data }) => {
        return {
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/levels/${levelid}/notes/${note_id}`,
            method: 'PATCH',
            attachAuth: true,
            body: levelNoteFormSchema.parse(data),
        };
    },
    invalidateQueries: ({ variables: { list, levelid } }) => [
        'list',
        list,
        levelid,
        'level_notes',
    ],

    toastMessages: {
        loading: 'Updating note...',
        error: 'Failed to update note: ',
        success: 'Note updated successfully',
    },
});

export type deleteNoteMutationType = {
    note_id: string;
    levelid: string;
    list: 'classic' | 'platformer';
};
export const deleteNoteMutation = (): ApiMutationOptions<
    LevelNote,
    deleteNoteMutationType
> => ({
    buildRequest: ({ note_id, levelid, list }) => {
        return {
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/levels/${levelid}/notes/${note_id}`,
            method: 'DELETE',
            attachAuth: true,
        };
    },
    invalidateQueries: ({ variables: { list, levelid } }) => [
        'list',
        list,
        levelid,
        'level_notes',
    ],
    toastMessages: {
        loading: 'Deleting note...',
        error: 'Failed to delete note: ',
        success: 'Note deleted successfully',
    },
});

export type createUpdateMutationType = {
    levelid: string;
    list: 'classic' | 'platformer';
    data: levelUpdateFormInput;
};
export const createUpdateMutation = (): ApiMutationOptions<
    LevelUpdate,
    createUpdateMutationType
> => ({
    buildRequest: ({ levelid, list, data }) => {
        return {
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/levels/${levelid}/updates`,
            method: 'POST',
            attachAuth: true,
            body: levelUpdateFormSchema.parse(data),
        };
    },
    invalidateQueries: ({ variables: { list, levelid } }) => [
        ['list', list, levelid, 'level_updates'],
    ],
    toastMessages: {
        loading: 'Adding update...',
        error: 'Failed to add update: ',
        success: 'Update added successfully',
    },
});

export type editUpdateMutationType = {
    update_id: string;
    levelid: string;
    list: 'classic' | 'platformer';
    data: levelUpdateFormInput;
};
export const editUpdateMutation = (): ApiMutationOptions<
    LevelUpdate,
    editUpdateMutationType
> => ({
    buildRequest: ({ update_id, levelid, list, data }) => {
        return {
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/levels/${levelid}/updates/${update_id}`,
            method: 'PATCH',
            attachAuth: true,
            body: levelUpdateFormSchema.parse(data),
        };
    },
    invalidateQueries: ({ variables: { list, levelid } }) => [
        'list',
        list,
        levelid,
        'level_updates',
    ],

    toastMessages: {
        loading: 'Updating update...',
        error: 'Failed to update update: ',
        success: 'Update updated successfully',
    },
});

export type deleteUpdateMutationType = {
    update_id: string;
    levelid: string;
    list: 'classic' | 'platformer';
};
export const deleteUpdateMutation = (): ApiMutationOptions<
    LevelUpdate,
    deleteUpdateMutationType
> => ({
    buildRequest: ({ update_id, levelid, list }) => {
        return {
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/levels/${levelid}/updates/${update_id}`,
            method: 'DELETE',
            attachAuth: true,
        };
    },
    invalidateQueries: ({ variables: { list, levelid } }) => [
        'list',
        list,
        levelid,
        'level_updates',
    ],
    toastMessages: {
        loading: 'Deleting update...',
        error: 'Failed to delete update: ',
        success: 'Update deleted successfully',
    },
});
